import {
  Box,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { SearchableMembershipTypeSelect } from '@/components/SearchableMembershipTypeSelect';
import { SearchableOrganizationAttributesSelect } from '@/components/SearchableOrganizationAttributesSelect';
import { SearchablePeopleAttributeSelect } from '@/components/SearchablePeopleAttributeSelect';
import {
  ContactAttributeInput,
  EGroupConditionLogic,
} from '@/graphql-types/globalTypes';

import {
  contactAttributesAreSelected,
  getContactAttributeLists,
} from '../../../../groupAttributeUtils';

// Options for additional checkboxes
const extraOptions = [
  {
    label: 'Contacts with selected People Attributes',
    value: 'people_attributes',
  },
  {
    label: 'Contacts with selected Company Attributes',
    value: 'company_attributes',
  },
  {
    label: 'Members with selected Membership Type',
    value: 'membership_type',
  },
];
const JoinConfigSelection = () => {
  const { watch, setValue } = useFormContext();

  const configSelectionRaw = watch('configSelection');
  const configSelection = Array.isArray(configSelectionRaw)
    ? configSelectionRaw
    : [];

  return (
    <Box w="full">
      <Controller
        name="configSelection"
        render={({ field: { onChange, value } }) => (
          <CheckboxGroup
            value={value || []}
            onChange={(newValue: string[]) => {
              // Reset related fields when config selection changes
              const oldValue = value || [];

              // Check for removed selections
              oldValue.forEach((selection: string) => {
                if (!newValue.includes(selection)) {
                  switch (selection) {
                    case 'people_attributes':
                      setValue('peopleAttributes', {
                        defaultAttributes: [],
                        customAttributes: [],
                      });
                      break;
                    case 'company_attributes':
                      setValue('companyAttributes', {
                        defaultAttributes: [],
                        customAttributes: [],
                      });
                      break;
                    case 'membership_type':
                      setValue('membershipTypeIds', []);
                      break;
                  }
                }
              });

              onChange(newValue);
            }}
          >
            <VStack alignItems="start" spacing="2">
              {extraOptions.map((option) => (
                <React.Fragment key={option.value}>
                  <Checkbox value={option.value}>{option.label}</Checkbox>

                  {/* People Attributes Selection */}
                  {option.value === 'people_attributes' &&
                    configSelection.includes('people_attributes') && (
                      <Controller
                        name="peopleAttributes"
                        render={({ field }) => {
                          const { defaultAttributes, customAttributes } =
                            getContactAttributeLists(field.value);

                          return (
                            <FormControl
                              isRequired
                              isInvalid={
                                !contactAttributesAreSelected(field.value)
                              }
                            >
                              <FormLabel>Select People Attribute</FormLabel>
                              <SearchablePeopleAttributeSelect
                                value={[
                                  ...defaultAttributes,
                                  ...customAttributes,
                                ]}
                                onChange={(newValue: ContactAttributeInput) => {
                                  field.onChange(newValue);
                                }}
                                onBlur={field.onBlur}
                              />
                              <FormErrorMessage>
                                Please select at least one people attribute
                              </FormErrorMessage>
                            </FormControl>
                          );
                        }}
                      />
                    )}

                  {/* Company Attributes Selection */}
                  {option.value === 'company_attributes' &&
                    configSelection.includes('company_attributes') && (
                      <Controller
                        name="companyAttributes"
                        render={({ field }) => {
                          const { defaultAttributes, customAttributes } =
                            getContactAttributeLists(field.value);

                          return (
                            <FormControl
                              isRequired
                              isInvalid={
                                !contactAttributesAreSelected(field.value)
                              }
                            >
                              <FormLabel>Select Company Attribute</FormLabel>
                              <SearchableOrganizationAttributesSelect
                                value={[
                                  ...defaultAttributes,
                                  ...customAttributes,
                                ]}
                                onChange={(newValue: ContactAttributeInput) => {
                                  field.onChange(newValue);
                                }}
                                onBlur={field.onBlur}
                              />
                              <FormErrorMessage>
                                Please select at least one company attribute
                              </FormErrorMessage>
                            </FormControl>
                          );
                        }}
                      />
                    )}

                  {/* Membership Type Selection */}
                  {option.value === 'membership_type' &&
                    configSelection.includes('membership_type') && (
                      <Controller
                        name="membershipTypeIds"
                        render={({ field }) => {
                          return (
                            <FormControl
                              isRequired
                              isInvalid={!field.value?.length}
                            >
                              <FormLabel>Select Membership Type</FormLabel>
                              <SearchableMembershipTypeSelect
                                value={field.value || []}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                              />
                              <FormErrorMessage>
                                Please select at least one membership type
                              </FormErrorMessage>
                            </FormControl>
                          );
                        }}
                      />
                    )}
                </React.Fragment>
              ))}

              {configSelection.length > 0 && (
                <FormControl mt={3}>
                  <FormLabel>Condition Logic</FormLabel>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    Choose how multiple selected attributes are combined within
                    People Attributes or within Company Attributes. People
                    Attributes, Company Attributes, and Membership Type are
                    always matched with OR.
                  </Text>
                  <Controller
                    name="conditionLogic"
                    defaultValue={EGroupConditionLogic.OR}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        value={value || EGroupConditionLogic.OR}
                        onChange={onChange}
                      >
                        <VStack alignItems="start" spacing="2">
                          <Radio value={EGroupConditionLogic.OR}>
                            Match any selected attribute (OR)
                          </Radio>
                          <Radio value={EGroupConditionLogic.AND}>
                            Match all selected attributes (AND)
                          </Radio>
                        </VStack>
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              )}
            </VStack>
          </CheckboxGroup>
        )}
      />
    </Box>
  );
};

export default JoinConfigSelection;
